import { ExportOutlined, PlusOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Flex, type MenuProps, Tooltip } from "antd";
import React from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import ActiveRecordingView from "../../components/ActiveRecordingView/ActiveRecordingView.component";
import ChangePassword from "../../components/ChangePassword/ChangePassword.component";
import ClientView from "../../components/ClientView/ClientView.component";
import { TabType, ViewType } from "../../components/CreateNoteModal/AITemplateModal/types";
import NudgeLogo from "../../components/NudgeLogo";
import { isFromSOL } from "../../domain/groups.domain";
import { isLockedOutOfApp } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import useIntercomWithData from "../../hooks/useIntercomWithData";
import {
  setAITemplateModalState,
  setCreateModalState,
  setCustomTemplateSelectedTab,
  setEditingState,
  setNotesViewVisible,
  setOpenShare,
  setSelectedClient,
  setSelectedView,
  setShowSettingsModal,
  setSidebarCollapsed,
} from "../../slices/appStateSlice";
import { UserTier, resetBasicUserInfo } from "../../slices/authSlice";
import type { AppDispatch } from "../../store";
import { EventType, trackEvent, trackServerEvent } from "../../utils/analytics";
import { clearSessionAndRedirect } from "../../utils/auth.utils";
import { STRIPE_BILLING_PORTAL_URL } from "../../utils/constants";
import ClientTimeline from "./ClientTimeline.component";
import styles from "./home.module.scss";

const componentName = "home";

interface SidebarProps {
  isPaused?: boolean;
  recordingTime?: number;
  handlePauseOrEndRecording?: () => void;
  handleResumeRecording?: () => void;
  handleSubmitAudio?: () => void;
  cancelRecording?: () => void;
  showClientList?: boolean;
}

const noop = () => {};

const Sidebar: React.FC<SidebarProps> = ({
  isPaused = false,
  recordingTime = 0,
  handlePauseOrEndRecording = noop,
  handleResumeRecording = noop,
  handleSubmitAudio = noop,
  cancelRecording = noop,
  showClientList = true,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { show: showIntercom } = useIntercomWithData();

  const { sidebarCollapsed, showRecordingView, selectedClient, superdocLayout } = useAppSelector(
    (state) => state.appState,
  );
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const userData = useAppSelector((state) => state.user.data);

  const isLocked = isLockedOutOfApp(userInfo);
  const [openChangePasswordModal, setOpenChangePasswordModal] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  // --- Navigation ---
  const navigateToHome = () => {
    navigate("/home");
    dispatch(setSelectedClient(null));
    dispatch(setNotesViewVisible(false));
    dispatch(setSelectedView(ViewType.DASHBOARD));
  };

  const openTemplateGallery = () => {
    dispatch(setSelectedView(ViewType.TEMPLATE));
    navigate("/templates");
  };

  const handleStartSession = () => {
    if (!location.pathname.startsWith("/home")) {
      navigate("/home");
    }
    dispatch(setEditingState(false));
    dispatch(setCreateModalState(true));
  };

  const openMyTemplates = () => {
    if (!location.pathname.startsWith("/home")) {
      navigate("/home");
    }
    dispatch(setAITemplateModalState(true));
    dispatch(setCustomTemplateSelectedTab(TabType.LIST));
  };

  const openShare = () => {
    trackEvent(EventType.SHARE_MODAL_OPEN, { shareModalType: "share_button" });
    trackServerEvent(EventType.SHARE_MODAL_OPEN, { shareModalType: "share_button" });
    dispatch(setOpenShare(true));
  };

  const contactUs = () => {
    trackEvent(EventType.CONTACT_US);
    if (isFromSOL(userInfo)) {
      window.open("https://tally.so/r/3jLbZa", "_blank");
    } else {
      window.open("https://tally.so/r/wg5QxK", "_blank");
    }
  };

  const logout = async () => {
    trackEvent(EventType.LOGOUT);
    await axiosInstance.post("/rest-auth/logout/", {}).catch((error) => {
      console.error(error);
    });
    dispatch(resetBasicUserInfo());
    clearSessionAndRedirect();
  };

  // --- Active state helpers ---
  const isDashboardActive =
    location.pathname === "/home" || location.pathname === "/" || location.pathname.startsWith("/home/");
  const isTemplateGalleryActive = location.pathname.startsWith("/templates");

  // --- Menu items ---
  const sidebarMenuItems: MenuProps["items"] = [
    {
      key: "settings",
      label: (
        <div style={{ color: "#000" }} onClick={() => dispatch(setShowSettingsModal(true))}>
          Settings
        </div>
      ),
    },
    ...(userInfo.is_paying || (userInfo.tier === UserTier.GROUP && userInfo.is_group_admin)
      ? [
          {
            key: "my-account",
            label: (
              <div onClick={() => window.open(STRIPE_BILLING_PORTAL_URL, "_blank")} style={{ color: "#000" }}>
                My Account
              </div>
            ),
          },
        ]
      : []),
    {
      key: "templates",
      label: (
        <div onClick={openTemplateGallery} style={{ color: "#000" }}>
          Template Gallery
        </div>
      ),
    },
    {
      key: "ai-receptionist",
      label: (
        <div
          onClick={() => {
            dispatch(setSelectedView(ViewType.AI_RECEPTIONIST));
            navigate("/call/dashboard");
          }}
          style={{ color: "#000" }}
        >
          AI Call Receptionist
        </div>
      ),
    },
    ...(!userInfo.is_paying || userInfo.group_name
      ? [
          {
            key: "support",
            label: (
              <div onClick={() => showIntercom()} style={{ color: "#000" }}>
                Support
              </div>
            ),
          },
        ]
      : []),
    {
      key: "new-features",
      label: (
        <a href="https://getnudgeai.com/release-notes" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          New Features <ExportOutlined />
        </a>
      ),
    },
    {
      key: "contact",
      label: (
        <div onClick={contactUs} style={{ color: "#000" }}>
          Contact Us
        </div>
      ),
    },
    {
      key: "share",
      label: (
        <div onClick={openShare} style={{ color: "#000" }}>
          Share & Earn Free a Month
        </div>
      ),
    },
    {
      key: "consent-form",
      label: (
        <a href="/pdf/client_consent_form.pdf" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          Consent Form <ExportOutlined />
        </a>
      ),
    },
    {
      key: "tutorial",
      label: (
        <a
          href={
            isFromSOL(userInfo)
              ? "https://www.youtube.com/playlist?list=PL9lb9UfUiaJwSF0u5fBx22xb_CwFExyKu"
              : "https://www.youtube.com/playlist?list=PL9lb9UfUiaJwUUCOBLgiznYWj8NT-7jD2"
          }
          target="_blank"
          style={{ color: "#000" }}
          rel="noreferrer"
        >
          Tutorial <ExportOutlined />
        </a>
      ),
    },
    {
      key: "ambassador",
      label: (
        <a href="https://tally.so/r/3xNd0G" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          Become an Influencer <ExportOutlined />
        </a>
      ),
    },
    {
      key: "change-password",
      label: (
        <div onClick={() => setOpenChangePasswordModal(true)} style={{ color: "#000" }}>
          Change Password
        </div>
      ),
    },
    {
      key: "logout",
      label: (
        <div onClick={logout} style={{ color: "red" }}>
          Logout
        </div>
      ),
    },
  ];

  // --- Icons (16px) ---
  const HomeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2.5 7.5L10 1.667 17.5 7.5V16.667a1.667 1.667 0 01-1.667 1.666H4.167A1.667 1.667 0 012.5 16.667V7.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 18.333V10h5v8.333"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const FolderIcon = () => (
    <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0.75 8.25V7.625C0.75 7.12772 0.947544 6.65081 1.29917 6.29917C1.65081 5.94754 2.12772 5.75 2.625 5.75H15.125C15.6223 5.75 16.0992 5.94754 16.4508 6.29917C16.8025 6.65081 17 7.12772 17 7.625V8.25M9.75833 2.88333L7.99167 1.11667C7.8756 1.00046 7.73778 0.908265 7.58607 0.845351C7.43436 0.782436 7.27174 0.750035 7.1075 0.75H2.625C2.12772 0.75 1.65081 0.947544 1.29917 1.29917C0.947544 1.65081 0.75 2.12772 0.75 2.625V12.625C0.75 13.1223 0.947544 13.5992 1.29917 13.9508C1.65081 14.3025 2.12772 14.5 2.625 14.5H15.125C15.6223 14.5 16.0992 14.3025 16.4508 13.9508C16.8025 13.5992 17 13.1223 17 12.625V5.125C17 4.62772 16.8025 4.15081 16.4508 3.79917C16.0992 3.44754 15.6223 3.25 15.125 3.25H10.6425C10.3111 3.24971 9.99253 3.11782 9.75833 2.88333Z"
        stroke="#71839B"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );

  const CollapseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1.5" y="2.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="1.5" y="2.5" width="5.5" height="15" rx="1" fill="currentColor" opacity="0.4" />
      <line x1="7" y1="2.5" x2="7" y2="17.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );

  const ExpandIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1.5" y="2.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="1.5" y="2.5" width="5.5" height="15" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  );

  const ChevronIcon = ({ up = false }: { up?: boolean }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ transition: "transform 0.2s ease", transform: up ? "rotate(0deg)" : "rotate(180deg)" }}
    >
      <path d="M4 10L8 6l4 4" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <>
      <div
        className={`${styles[`${componentName}__container-sidebar`]} ${sidebarCollapsed ? styles[`${componentName}__container-sidebar--collapsed`] : ""}`}
      >
        {/* Collapsed sidebar */}
        <div className={styles[`${componentName}__container-sidebar-collapsed-content`]}>
          <Flex vertical align="center" gap={12}>
            <Tooltip title="Expand Sidebar" placement="right">
              <Button
                type="text"
                className={styles[`${componentName}__container-side-collapsed-icon`]}
                icon={<ExpandIcon />}
                onClick={() => dispatch(setSidebarCollapsed(false))}
              />
            </Tooltip>
            <Tooltip title="Nudge AI" placement="right">
              <div
                className={styles[`${componentName}__container-side-collapsed-logo`]}
                onClick={navigateToHome}
                style={{ cursor: "pointer", fontWeight: 800, fontSize: 22, color: "#6366F1" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigateToHome()}
              >
                n
              </div>
            </Tooltip>
            {!showRecordingView && (
              <>
                <Tooltip title="Your Dashboard" placement="right">
                  <Button
                    type="text"
                    className={`${styles[`${componentName}__container-side-collapsed-icon`]} ${isDashboardActive ? styles[`${componentName}__container-side-collapsed-icon--active`] : ""}`}
                    onClick={navigateToHome}
                    icon={<HomeIcon />}
                  />
                </Tooltip>
                <Tooltip title="Template Gallery" placement="right">
                  <Button
                    type="text"
                    className={`${styles[`${componentName}__container-side-collapsed-icon`]} ${isTemplateGalleryActive ? styles[`${componentName}__container-side-collapsed-icon--active`] : ""}`}
                    onClick={openTemplateGallery}
                    icon={<FolderIcon />}
                  />
                </Tooltip>
              </>
            )}
            {showRecordingView ? (
              <Tooltip title="Recording in progress — click to expand" placement="right">
                <Button
                  type="text"
                  className={styles[`${componentName}__container-side-collapsed-mic-btn`]}
                  onClick={() => dispatch(setSidebarCollapsed(false))}
                  icon={
                    <span className={styles[`${componentName}__container-side-collapsed-mic`]}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 1a4 4 0 00-4 4v7a4 4 0 008 0V5a4 4 0 00-4-4z" fill="white" />
                        <path
                          d="M19 10v2a7 7 0 01-14 0v-2"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <line x1="12" y1="19" x2="12" y2="23" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                  }
                />
              </Tooltip>
            ) : (
              <Tooltip title="Start a Session" placement="right">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusOutlined style={{ fontSize: 12 }} />}
                  className={styles[`${componentName}__container-side-collapsed-add`]}
                  onClick={handleStartSession}
                />
              </Tooltip>
            )}
          </Flex>
          <Flex vertical align="center" style={isLocked ? { position: "relative", zIndex: 1101 } : undefined}>
            <Dropdown
              menu={{ items: sidebarMenuItems }}
              placement="topLeft"
              trigger={["click"]}
              overlayStyle={{ zIndex: 1101 }}
            >
              <Tooltip title="Settings" placement="right">
                {userInfo.group_logo ? (
                  <img
                    height={28}
                    width="auto"
                    src={userInfo.group_logo as string}
                    alt={userInfo.group_name || ""}
                    style={{ borderRadius: 4, flexShrink: 0, border: "1px solid #E5E7EB", cursor: "pointer" }}
                  />
                ) : (
                  <Avatar
                    size={32}
                    style={{
                      backgroundColor: "#6366F1",
                      fontSize: 14,
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  >
                    {(userData?.name?.[0] || "U").toUpperCase()}
                  </Avatar>
                )}
              </Tooltip>
            </Dropdown>
          </Flex>
        </div>

        {/* Expanded sidebar */}
        <div className={styles[`${componentName}__container-sidebar-expanded-content`]}>
          <div className={styles[`${componentName}__container-sidebar-header`]}>
            <div className={styles[`${componentName}__container-sidebar-header-logo-box`]}>
              <NudgeLogo practiceArea={userInfo.practice_area} onClick={navigateToHome} fontSize={16} />
              <Button
                type="text"
                className={styles[`${componentName}__container-sidebar-toggle`]}
                icon={<CollapseIcon />}
                onClick={() => dispatch(setSidebarCollapsed(true))}
              />
            </div>
          </div>

          {showRecordingView && (
            <Flex vertical align="center" className={styles[`${componentName}__container-sidebar-recording-padding`]}>
              <ActiveRecordingView
                isPaused={isPaused}
                recordingTime={recordingTime}
                handlePauseOrEndRecording={handlePauseOrEndRecording}
                handleResumeRecording={handleResumeRecording}
                handleSubmitAudio={handleSubmitAudio}
                cancelRecording={cancelRecording}
              />
            </Flex>
          )}

          {!showRecordingView && (
            <nav className={styles[`${componentName}__container-sidebar-nav`]}>
              <div
                className={`${styles[`${componentName}__container-sidebar-nav-item`]} ${isDashboardActive ? styles[`${componentName}__container-sidebar-nav-item--active`] : ""}`}
                onClick={navigateToHome}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigateToHome()}
              >
                <HomeIcon />
                <span>Your Dashboard</span>
              </div>
              <div
                className={`${styles[`${componentName}__container-sidebar-nav-item`]} ${isTemplateGalleryActive ? styles[`${componentName}__container-sidebar-nav-item--active`] : ""}`}
                onClick={openTemplateGallery}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openTemplateGallery()}
              >
                <FolderIcon />
                <span>Template Gallery</span>
              </div>

              <div
                className={`${styles[`${componentName}__container-sidebar-nav-item`]} ${styles[`${componentName}__container-sidebar-start-session`]}`}
                onClick={handleStartSession}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleStartSession()}
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="8" fill="#6366F1" />
                  <path d="M8 4.5V11.5M4.5 8H11.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>Start a Session</span>
              </div>
            </nav>
          )}

          <div className={styles[`${componentName}__container-sidebar-body`]}>
            {showClientList &&
              (superdocLayout === "superdoc-center" ? (
                // Superdoc-center: the notes list lives in the sidebar (per-patient
                // timeline when a patient is selected, otherwise the patient list).
                <div
                  key={selectedClient ? `client-${selectedClient.id}` : "client-list"}
                  className={`${styles[`${componentName}__container-sidebar-body-view`]} ${selectedClient ? styles[`${componentName}__container-sidebar-body-view--timeline`] : ""}`}
                >
                  {selectedClient ? <ClientTimeline /> : <ClientView />}
                </div>
              ) : (
                // Notes-center: the sidebar is just the patient list; the selected
                // patient's notes render front-and-center (see NotesSection).
                <div className={styles[`${componentName}__container-sidebar-body-view`]}>
                  <ClientView />
                </div>
              ))}
          </div>

          <div
            className={styles[`${componentName}__container-sidebar-footer`]}
            style={isLocked ? { position: "relative", zIndex: 1101 } : undefined}
          >
            <Dropdown
              menu={{ items: sidebarMenuItems, style: { padding: 4 } }}
              placement="topRight"
              trigger={["click"]}
              overlayStyle={{ zIndex: 1101, minWidth: "auto" }}
              onOpenChange={setDropdownOpen}
              open={dropdownOpen}
            >
              <Flex align="center" gap={10} className={styles[`${componentName}__container-sidebar-footer-profile`]}>
                {userInfo.group_logo ? (
                  <img
                    height={28}
                    width="auto"
                    src={userInfo.group_logo as string}
                    alt={userInfo.group_name || ""}
                    style={{ borderRadius: 4, flexShrink: 0, border: "1px solid #E5E7EB" }}
                  />
                ) : (
                  <Avatar
                    size={32}
                    style={{
                      backgroundColor: "#6366F1",
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {(userData?.name?.[0] || userData?.name?.[0] || "U").toUpperCase()}
                  </Avatar>
                )}
                <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                  <span className={styles[`${componentName}__container-sidebar-footer-name`]}>
                    {userData?.name ? `${userData.name || ""}`.trim() : userData?.email || "User"}
                  </span>
                  <span className={styles[`${componentName}__container-sidebar-footer-email`]}>
                    {userData?.email || ""}
                  </span>
                </Flex>
                <span className={styles[`${componentName}__container-sidebar-footer-chevron`]}>
                  <ChevronIcon up={dropdownOpen} />
                </span>
              </Flex>
            </Dropdown>
          </div>
        </div>
      </div>

      {openChangePasswordModal && (
        <ChangePassword show={openChangePasswordModal} onClose={() => setOpenChangePasswordModal(false)} />
      )}
    </>
  );
};

export default Sidebar;
